/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { matchPath } from 'react-router-dom';
import classNames from 'classnames';

import DayPicker from 'react-day-picker';
import TimePicker from 'rc-time-picker';
import moment from 'moment-timezone';
// @ts-ignore - Getting issue - resolution field "graceful-fs@4.2.8" is incompatible with requested version "graceful-fs@~3.0.5"
import dateArray from 'moment-array-dates';
import RoleCardDoneButton from './DoneButton';
import RoleCardMultipleButton from './MultipleButton';
import { toggleOptionCard } from '../../../actions/ViewController';
import { getTime } from '../../../api/DataHandlers';
import { usePrevious } from '../../../hooks';
import { TRootState } from '../../../reducers';
import history from '../../../history';

type TTimeDateCard = {
    calendarOnly: boolean;
    updateField: (
        field: string,
        data: string | number | boolean | string[],
    ) => void;
    className?: string;
};

export default function TimeDateCard({
    calendarOnly,
    updateField,
    className,
}: TTimeDateCard) {
    const [mouseDown, setMouseDown] = useState(false);
    const [fromDate, setFromDate] = useState(null);

    const dispatch = useDispatch();

    const {
        calendar,
        rangeDatesSelected,
        isMultiple,
        start_time,
        minStart,
        duration,
        isRemoving,
        dates,
        date,
    } = useSelector((state: TRootState) => ({
        calendar: state.view.calendar,
        rangeDatesSelected: state.requestData.bookingRequest.rangeDatesSelected,
        isMultiple: state.requestData.bookingRequest.isMultiple,
        start_time: state.requestData.bookingRequest.start_time,
        minStart: state.requestData.minStart,
        duration: state.requestData.bookingRequest.duration,
        isRemoving: state.requestData.bookingRequest.isRemoving,
        dates: state.requestData.bookingRequest.dates,
        date: state.requestData.bookingRequest.date,
    }));

    const prevDate = usePrevious(date);
    const prevStartTime = usePrevious(start_time);

    const checkDateTimeValid = useCallback(() => {
        const dateTime = `${date} ${start_time}`;
        const m = moment(dateTime, 'YYYY-MM-DD HH:mm');

        return m.isAfter(minStart) || m.isSame(minStart);
    }, [date, minStart, start_time]);

    useEffect(() => {
        if (prevDate !== date || prevStartTime !== start_time) {
            if (!checkDateTimeValid()) {
                const timeFormatted = moment(minStart).format('HH:mm');
                const dateFormatted = moment(minStart)
                    .toISOString()
                    .slice(0, 10);
                dispatch(updateField('start_time', timeFormatted));

                const CLIENT_JOB_ROUTE = '/client';
                const isThisClientJobRoute = !!matchPath(
                    history.location.pathname,
                    CLIENT_JOB_ROUTE,
                );

                if (!isThisClientJobRoute) {
                    if (!date) dispatch(updateField('date', dateFormatted));
                }
            }
        }
    }, [
        checkDateTimeValid,
        date,
        dispatch,
        minStart,
        prevDate,
        prevStartTime,
        start_time,
        updateField,
    ]);

    const handleDayClick = useCallback(
        (dateFromCalendar: { toISOString: () => string | any[] }) => {
            const dateFormatted = dateFromCalendar.toISOString().slice(0, 10);
            dispatch(updateField('date', dateFormatted));

            if (calendarOnly) dispatch(toggleOptionCard());
        },
        [calendarOnly, dispatch, updateField],
    );

    const handleStartChange = useCallback(
        (value: { format: (arg0: string) => any }) => {
            const startTime = value?.format('HH:mm');
            dispatch(updateField('start_time', startTime));
        },
        [dispatch, updateField],
    );

    const handleEndChange = useCallback(
        (value: { format: (arg0: string) => any }) => {
            const endTime = value?.format('HH:mm');
            let durationTime = moment
                .duration(endTime)
                // @ts-ignore
                .subtract(start_time, 'HH:mm')
                .as('hours');

            if (durationTime < 0 && durationTime >= -24) durationTime += 24;
            if (durationTime >= 3 && durationTime <= 16) {
                dispatch(updateField('duration', durationTime));
            }
        },
        [dispatch, start_time, updateField],
    );

    const handleMinutePickerOpen = useCallback((value: { open: any }) => {
        if (value.open) {
            setTimeout(() => {
                // It's needed because of race conditions in a browser
                document
                    .querySelector('.rc-time-picker-panel')
                    .classList.add('minute-picker-panel');
            }, 0);
        }
    }, []);

    const handleDayMouseDown = useCallback(
        (
            day: moment.MomentInput,
            modifiers: { past: any; outside: any; selected: any },
        ) => {
            if (
                modifiers.past ||
                modifiers.outside ||
                moment(day).isBefore(moment(minStart))
            )
                return;

            dispatch(
                updateField('rangeDatesSelected', [
                    moment(day).format('YYYY-MM-DD'),
                ]),
            );

            const areSelectedDatesRemovedFromRangeOfDates =
                !!modifiers.selected;
            dispatch(
                updateField(
                    'isRemoving',
                    areSelectedDatesRemovedFromRangeOfDates,
                ),
            );

            setMouseDown(true);
            setFromDate(day);
        },
        [dispatch, minStart, updateField],
    );

    const handleDayMouseUp = useCallback(() => {
        if (isRemoving) {
            dispatch(
                updateField(
                    'dates',
                    dates.filter(
                        // eslint-disable-next-line no-shadow
                        (date: string) => !rangeDatesSelected.includes(date),
                    ),
                ),
            );
        } else {
            dispatch(
                updateField(
                    'dates',
                    Array.from(new Set([...dates, ...rangeDatesSelected])),
                ),
            );
        }

        dispatch(updateField('rangeDatesSelected', []));

        setMouseDown(false);
        setFromDate(null);
    }, [dates, dispatch, isRemoving, rangeDatesSelected, updateField]);

    const handleDayMouseEnter = useCallback(
        (toDate: moment.MomentInput) => {
            if (!mouseDown) return;

            if (fromDate.toString() === toDate.toString()) {
                dispatch(
                    updateField('rangeDatesSelected', [
                        moment(fromDate).format('YYYY-MM-DD'),
                    ]),
                );
                return;
            }

            let from;
            let to;
            if (moment(fromDate).isBefore(moment(toDate))) {
                from = fromDate;
                to = toDate;
            } else {
                from = moment(toDate).isBefore(moment(minStart))
                    ? moment(minStart).format('YYYY-MM-DD')
                    : toDate;
                to = fromDate;
            }
            const datesRange = dateArray.range(from, to, 'YYYY-MM-DD', true);

            dispatch(updateField('rangeDatesSelected', datesRange));
        },
        [dispatch, fromDate, minStart, mouseDown, updateField],
    );

    const handleDisabledHours = useCallback(() => {
        const dateNew = moment(date);
        const minStartMoment = moment(minStart);

        if (dateNew.isSame(minStartMoment, 'day')) {
            const disabledHoursArr = [];
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < minStartMoment.hour(); i++) {
                disabledHoursArr.push(i);
            }
            return disabledHoursArr;
        }

        return [];
    }, [date, minStart]);

    const handleDisabledMinutes = useCallback(
        (h: number) => {
            const minStartMoment = moment(minStart);
            const minMinute = minStartMoment.minute();
            const minHour = minStartMoment.hour();
            const dateMoment = moment(date);

            function generateOptions(length: number, excludedOptions: number) {
                const arr = [];
                // eslint-disable-next-line no-plusplus
                for (let value = 0; value < length; value++) {
                    if (excludedOptions > value) {
                        arr.push(value);
                    }
                }
                return arr;
            }

            if (dateMoment.isSame(minStartMoment, 'day')) {
                if (h === minHour) {
                    return generateOptions(60, minMinute);
                }
            }

            return [];
        },
        [date, minStart],
    );

    let days: string[] = null;

    if (!isRemoving) {
        days = dates.concat(rangeDatesSelected);
    } else {
        days = dates.filter(
            // eslint-disable-next-line no-shadow
            (date: string) => !rangeDatesSelected.includes(date),
        );
    }

    const initialStartTime =
        start_time === null ? getTime(minStart) : start_time;
    const startTime = moment(initialStartTime, 'HH:mm');
    const endTime = moment(initialStartTime, 'HH:mm').add(duration, 'h');

    let selectedDays = null;
    let modifiers;

    if (isMultiple) {
        selectedDays = (day: string) =>
            days.includes(moment(day).format('YYYY-MM-DD'));
        modifiers = {
            'selected-right': (day: string) =>
                days.includes(moment(day).add(1, 'd').format('YYYY-MM-DD')),
            'selected-left': (day: string) =>
                days.includes(moment(day).add(-1, 'd').format('YYYY-MM-DD')),
        };
    } else {
        selectedDays = moment(date, 'YYYY-MM-DD').toDate();
    }

    return (
        <div className={classNames('card card-role-timedate', className)}>
            <div className="card-content">
                <div className="items-container">
                    {calendar !== 'today' && calendar !== 'tomorrow' && (
                        // @ts-ignore
                        <DayPicker
                            weekdaysShort={['S', 'M', 'T', 'W', 'T', 'F', 'S']}
                            selectedDays={selectedDays}
                            firstDayOfWeek={1}
                            fixedWeeks={true}
                            fromMonth={new Date()}
                            enableOutsideDays={true}
                            modifiers={modifiers}
                            onDayClick={!isMultiple ? handleDayClick : null}
                            onDayMouseDown={
                                isMultiple ? handleDayMouseDown : null
                            }
                            onDayMouseUp={isMultiple ? handleDayMouseUp : null}
                            onDayMouseEnter={
                                isMultiple ? handleDayMouseEnter : null
                            }
                            // eslint-disable-next-line no-shadow
                            captionElement={({ date }: { date: any }) => (
                                <div className="DayPicker-Caption">
                                    {moment(date).format('MMMM')}
                                </div>
                            )}
                        />
                    )}
                    {!calendarOnly && (
                        <div className="role-time-picker">
                            <span>
                                From
                                <TimePicker
                                    format="HH"
                                    showSecond={false}
                                    showMinute={false}
                                    value={startTime}
                                    onChange={handleStartChange}
                                    disabledHours={handleDisabledHours}
                                />
                                <TimePicker
                                    format="mm"
                                    showSecond={false}
                                    showHour={false}
                                    value={startTime}
                                    onChange={handleStartChange}
                                    onOpen={handleMinutePickerOpen}
                                    disabledMinutes={handleDisabledMinutes}
                                />
                            </span>
                            <span>
                                to
                                <TimePicker
                                    format="HH"
                                    showSecond={false}
                                    showMinute={false}
                                    value={endTime}
                                    onChange={handleEndChange}
                                />
                                <TimePicker
                                    format="mm"
                                    showSecond={false}
                                    showHour={false}
                                    value={endTime}
                                    onChange={handleEndChange}
                                    onOpen={handleMinutePickerOpen}
                                />
                            </span>
                        </div>
                    )}
                    {!calendarOnly && (
                        <div className="role-button-container">
                            {calendar !== 'today' &&
                                calendar !== 'tomorrow' && (
                                    <RoleCardMultipleButton />
                                )}
                            <RoleCardDoneButton />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
